import { format } from 'date-fns';
import dateFnsTz from 'date-fns-tz';
import { getTimeZoneName, isLocalTimeZone, getTimeZoneOffsetMs, normalizeTimeZone } from './time-zone-utils.js';
import { ONE_SECOND, ONE_HOUR, ONE_MINUTE, ONE_DAY, ONE_YEAR } from './constants.js';

// timezone-standardization
// eslint-disable-next-line no-restricted-imports
const { formatInTimeZone } = dateFnsTz;
const TIME_FORMAT = 'hh:mm:ss a';
const DATE_TIME_FORMAT = `MM/dd/yyyy ${TIME_FORMAT}`;
const VERBOSE_DATE_TIME_FORMAT = `MMM d, yyyy, h:mm:ss a`;
const DATE_FORMAT = `M/d/yyyy`;
const SHORT_TIME_FORMAT = 'h:mm:ss a';
function getVerboseFormattedDateString(timeString, timeZone) {
    try {
        const abbr = getTimeZoneName(timeZone, {
            variant: 'short'
        });
        return `${formatWithTimeZone(new Date(timeString), VERBOSE_DATE_TIME_FORMAT, timeZone)} ${abbr}`;
    } catch  {
        return '';
    }
}
function spansMultipleDays(minTime, maxTime) {
    const formatter = (time)=>format(new Date(time), 'MM/dd/yyyy');
    return formatter(minTime) !== formatter(maxTime);
}
function getBetweenDateSpan(minTime, maxTime, timeZone) {
    const formattedMinDate = getFormattedDateString(minTime, timeZone);
    const formattedMaxDate = getFormattedDateString(maxTime, timeZone);
    if (formattedMinDate === '' || formattedMaxDate === '') {
        return '';
    }
    return spansMultipleDays(minTime, maxTime) ? `${formattedMinDate} - ${formattedMaxDate}` : `${formattedMinDate}`;
}
function getFormattedDateString(timeString, timeZone) {
    try {
        return formatWithTimeZone(new Date(timeString), DATE_FORMAT, timeZone);
    } catch  {
        return '';
    }
}
function getFormattedTimeString(timeString, timeZone) {
    try {
        return formatWithTimeZone(new Date(timeString), SHORT_TIME_FORMAT, timeZone);
    } catch  {
        return '';
    }
}
/**
 * Formats the date using the specified time zone.
 */ function formatWithTimeZone(date, formatString, timeZone) {
    const normalizedTimeZone = normalizeTimeZone(timeZone);
    if (isLocalTimeZone(normalizedTimeZone)) {
        return format(date, formatString);
    } else {
        return formatInTimeZone(date, normalizedTimeZone, formatString);
    }
}
function dateFormatOptionsWithTimeZone(dateFormatOptions, timeZone) {
    /*
   * if timeZone is provided, and is not local|browser,
   * then set timeZone option (recognize UTC regardless of uppercase/lowercase)
   * otherwise, default to browser timeZone setting
   */ if (timeZone) {
        const lowerTimeZone = timeZone.toLowerCase();
        if (lowerTimeZone !== 'local' && lowerTimeZone !== 'browser') {
            return {
                ...dateFormatOptions,
                timeZone: lowerTimeZone === 'utc' ? 'UTC' : timeZone
            };
        }
    }
    return dateFormatOptions;
}
function padTime(value) {
    return value.toString().padStart(2, '0');
}
/**
 * Returns a string that represents the UTC offset for the specified time zone.
 */ function formatTimeZoneOffset(timeZone, { variant = 'long' } = {}) {
    const offsetMs = getTimeZoneOffsetMs(timeZone);
    if (offsetMs === 0) {
        return 'UTC';
    }
    const sign = offsetMs > 0 ? '+' : '-';
    const hours = Math.floor(Math.abs(offsetMs) / ONE_HOUR);
    const minutes = Math.floor(Math.abs(offsetMs) % ONE_HOUR / ONE_MINUTE);
    let result = 'UTC' + sign;
    if (variant === 'short') {
        // `short` variant does not pad hours with zeros.
        result += hours;
    } else {
        // `long` variant pads hours with zeros.
        result += padTime(hours);
    }
    // Always include minutes when using `long` variant. Only include minutes for
    // `short` variant if they are meaningful (i.e. not zero).
    if (variant !== 'short' || minutes > 0) {
        result += ':' + padTime(minutes);
    }
    return result;
}
function formatAgo(thenMillis, nowMillis) {
    const diffMillis = Math.trunc(nowMillis - thenMillis);
    if (diffMillis <= 0) {
        return 'now';
    } else if (diffMillis < ONE_SECOND) {
        return `${diffMillis}ms ago`;
    } else if (diffMillis <= 120 * ONE_SECOND) {
        return `${Math.trunc(diffMillis / ONE_SECOND)}s ago`;
    } else if (diffMillis <= 2 * ONE_HOUR) {
        return `${Math.trunc(diffMillis / ONE_MINUTE)}m ago`;
    } else if (diffMillis <= 2 * ONE_DAY) {
        const hours = Math.trunc(diffMillis / ONE_HOUR);
        return `${hours}h${Math.trunc((diffMillis - hours * ONE_HOUR) / ONE_MINUTE)}m ago`;
    } else if (diffMillis <= 365 * 5 * ONE_DAY) {
        const days = Math.trunc(diffMillis / ONE_DAY);
        return `${days}d${Math.trunc((diffMillis - days * ONE_DAY) / ONE_HOUR)}h ago`;
    } else {
        const years = Math.trunc(diffMillis / ONE_YEAR);
        return `${years}y${Math.trunc((diffMillis - years * ONE_YEAR) / ONE_DAY)}d ago`;
    }
}
/**
 * Returns a compact duration string (e.g. 9h 6m)
 * To get a human readable duration string (e.g. 9 hours 6 minutes), use formatDuration from date-fns.
 */ function formatDurationCompact(x) {
    if (x < 1.0) {
        return `${parseInt(String(x * 1000), 10)}ms`;
    } else if (x < 60.0) {
        return `${x.toFixed(1)}s`;
    } else if (x < 3600.0) {
        const minutes = Math.floor(x / 60);
        const seconds = Math.floor(x % 60);
        return `${minutes}m ${seconds}s`;
    } else {
        const hours = Math.floor(x / 3600);
        const minutes = Math.floor(x % 3600 / 60);
        return `${hours}h ${minutes}m`;
    }
}
function formatMicroseconds(microseconds) {
    if (microseconds > 1_000_000) {
        return `${(microseconds / 1_000_000).toFixed(2)}s`;
    }
    if (microseconds > 1_000) {
        return `${(microseconds / 1_000).toFixed(2)}ms`;
    }
    return `${microseconds}μs`;
}
function parseSecondsSinceEpoch(seconds, nowFn) {
    const millisSinceEpoch = seconds * 1000.0;
    const date = new Date(millisSinceEpoch);
    const isoString = date.toISOString();
    return {
        date,
        agoStr: formatAgo(millisSinceEpoch, (nowFn && nowFn().getTime()) ?? new Date().getTime()),
        isoString
    };
}
const convertHoursToDays = (rawHours)=>{
    if (rawHours === null || rawHours === undefined || rawHours === '') return '';
    const totalHours = Number(rawHours.split('h')[0]);
    const fullDays = Math.trunc(totalHours / 24);
    const remainingHours = totalHours - fullDays * 24;
    if (remainingHours > 0) {
        return `${fullDays}d ${remainingHours}h`;
    }
    return `${fullDays}d`;
};
function dateTimetoEpoch(dateTimeStr) {
    return new Date(dateTimeStr).getTime() / 1000;
}

export { DATE_TIME_FORMAT, SHORT_TIME_FORMAT, TIME_FORMAT, convertHoursToDays, dateFormatOptionsWithTimeZone, dateTimetoEpoch, formatAgo, formatDurationCompact, formatMicroseconds, formatTimeZoneOffset, formatWithTimeZone, getBetweenDateSpan, getFormattedDateString, getFormattedTimeString, getVerboseFormattedDateString, parseSecondsSinceEpoch };
//# sourceMappingURL=formatters.js.map
